import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FolderOpen, MapPin, Plus, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import OperationsMasterDetailPage from "@/components/operations/OperationsMasterDetailPage";
import OperationsProjectDetail from "@/pages/operations/OperationsProjectDetail";
import {
    SLA_FILTER_OPTIONS, SLA_STATUS_LABELS, OPS004B_FILTER_KEYS,
    formatTimeInStage, buildStageFilterOptions, stageLabelMap,
} from "@/lib/stages";
import useSWR from "swr";

/**
 * `/operations/projects` — reference implementation for the DEV-003
 * Operations master/detail rollout.
 *
 * Reuses the existing `/api/projects/summary` endpoint (already accessible
 * to the `operations` role via the auto-`installer` role bridge in
 * `require_role`). No installer view is touched.
 *
 * The detail body lives in `OperationsProjectDetail.jsx` (DEV-003A) —
 * this file only fetches, filters and orchestrates selection.
 */

const STATE_CHIPS = [
    { key: "all",       label: "All" },
    { key: "active",    label: "Active" },
    { key: "on_hold",   label: "On hold" },
    { key: "completed", label: "Completed" },
];

// Canonical set of project-state chip keys that map to a real filter (i.e.
// every chip EXCEPT "all"). Kept in sync with STATE_CHIPS above at import
// time so the URL guard and the chip list can never drift.
const VALID_STATE_QUERY = new Set(
    STATE_CHIPS.map((c) => c.key).filter((k) => k !== "all"),
);

const STATE_LABEL = {
    active: "Active",
    on_hold: "On hold",
    completed: "Completed",
};

const STATE_DOT = {
    active: "bg-[#3E9E2E]",
    on_hold: "bg-[#F5A524]",
    completed: "bg-[#4B5565]",
};

// DEV-018 · Sales Source label fallback used by the Pipeline list row
// (renders the friendly label without an extra fetch). The canonical
// catalogue lives in ``backend/core/sales_source.py`` and is served
// via ``GET /api/projects/sales-sources`` — this fallback exists so
// the row never flashes a raw key while the catalogue is loading.
const SALES_SOURCE_LABELS_FALLBACK = {
    direct_customer:     "Direct Customer",
    outbound_call:       "Outbound Call",
    website:             "Website",
    referral:            "Referral",
    existing_customer:   "Existing Customer",
    social_media:        "Social Media",
    event_show:          "Event / Show",
    partner_introducer:  "Partner / Introducer",
    other:               "Other",
};

/**
 * OPS-002B · **Canonical front-end filter registry.**
 *
 * Every URL param that meaningfully filters the projects list lives here — and
 * ONLY here. Deep-link keys (`project`, `reviewSurvey`) are intentionally
 * excluded because they don't influence the query.
 *
 * Four consumers must all iterate this list so they can never drift:
 *   1. `loadProjects` — forwards each key to `api.operationsProjectsQuery`.
 *   2. `activeFilterCount` — the "N filters active" badge (excludes `q`,
 *      which is a free-text search, not a discrete filter chip).
 *   3. `resetFilters` — clears every canonical key from the URL.
 *   4. "Save as view" payload — serialises the exact filter combination.
 *
 * Keep this list in sync with the server contract in
 * `backend/routers/operations_queues.py::query_projects`.
 */
const CANONICAL_FILTERS = [
    "queue", "state", "engineer_id",
    "assigned", "overdue", "survey",
    "dno", "logistics", "customer_action",
    "window",
    // OPS-004B · Canonical stage filters (see `web/src/lib/stages.js`).
    ...OPS004B_FILTER_KEYS,
    // DEV-018 · Commercial attribution filters (Sales Owner + Sales
    // Source). "unassigned" is a first-class value alongside canonical
    // ids/keys so operators can slice by "no owner" or "no source".
    "sales_owner", "sales_source",
    // DEV-019 · Sales workflow filters.
    "outcome", "action_due", "action_type",
    // DEV-019 UAT3 · Canonical Sales lifecycle stage filter.
    "sales_stage",
    "q",
];

// Filter keys that count toward the "N filters active" badge — `q` is a
// free-text search, so it deliberately does not increment the badge.
const BADGE_FILTERS = CANONICAL_FILTERS.filter((k) => k !== "q");

export default function OperationsProjects() {
    const { hasPermission } = useAuth();
    const canCreate = hasPermission("manage_projects");
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    // SALES-ROLE-001B · Track the load failure separately so a
    // forbidden/failed query never collapses into "Nothing matches
    // the current filters" — that phrasing is reserved for a real
    // empty result. On error we render the retry banner instead.
    const [loadError, setLoadError] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    // DEV-010D · Deep-link support. When a notification links to
    // `/operations/projects?project=<pid>&reviewSurvey=<sid>` we auto-select
    // that project row and hand `reviewSurveyId` through the detail so the
    // DEV-010C review drawer opens automatically. Params survive refresh.
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const deepLinkProjectId = searchParams.get("project");
    const reviewSurveyId = searchParams.get("reviewSurvey");
    // OPS-003B2 · overdue-task deep link → focus the exact task in
    // its phase. Threaded through OperationsProjectDetail; invalid
    // ids surface a clear banner rather than a dashboard fallback.
    const focusTaskId = searchParams.get("taskId");
    // OPS-DASHBOARD-DEEP-LINKS · dashboard hero cards land here with
    // an actionable filter but no `?project=<id>`. When true, this
    // page auto-selects the first deterministic result and mirrors
    // that stable id back into the URL so refresh/share reproduces
    // the same detail pane. `focusStage=1` additionally asks the
    // detail pane to expand the current phase and highlight the
    // canonical next-action task.
    const focusStage = searchParams.get("focusStage") === "1";
    // Whitelist of dashboard-tile filter params. When ANY of these
    // appears (and no explicit `project=`), we auto-select the first
    // filtered result rather than leaving the user stranded on the
    // "Select a record" empty state.
    const DASHBOARD_FILTER_PARAMS = [
        "stalled_only", "stage_sla_status", "current_stage",
        "assigned", "queue", "focusStage",
    ];
    const hasDashboardFilter = DASHBOARD_FILTER_PARAMS.some(
        (k) => searchParams.get(k) != null,
    );

    // OPS-001A · The project-state filter is now URL-driven so that the
    // Reports Hub KPI drill-through (`?state=active` etc.) actually applies,
    // and browser Back/Forward keeps the chip and list in sync. The URL is
    // the single source of truth — no separate `useState` for the chip.
    const rawStateQuery = searchParams.get("state");
    const state = rawStateQuery && VALID_STATE_QUERY.has(rawStateQuery)
        ? rawStateQuery
        : "all";

    // OPS-002B · Every filter is derived from URL through the
    // `CANONICAL_FILTERS` registry declared at module scope. The URL is the
    // single source of truth so a shared link, a Reports-Hub drill-through
    // and a Saved View all produce identical query results.
    //
    // `filterValues` is a plain object keyed by canonical filter → raw URL
    // string (or `null`). Downstream consumers treat "" / null / undefined
    // identically as "not set".
    const filterValues = useMemo(() => {
        const out = {};
        for (const k of CANONICAL_FILTERS) out[k] = searchParams.get(k) || null;
        return out;
    }, [searchParams]);

    // Free-text search — the input is bound to URL param `q` so it
    // participates in saved views and shareable URLs, but we keep a local
    // "draft" string so keystrokes don't hammer the router on every letter.
    const urlQ = filterValues.q || "";
    const [query, setQuery] = useState(urlQ);
    useEffect(() => { setQuery(urlQ); }, [urlQ]);

    // OPS-004B1 · Stage catalogue fetched from the canonical
    // backend endpoint — no hardcoded progression list.
    const { data: stageCatalogue } = useSWR(
        "ops-stages-catalogue",
        () => api.operationsStageCatalogue(),
        { revalidateOnFocus: false, shouldRetryOnError: false },
    );
    const stageFilterOptions = useMemo(
        () => buildStageFilterOptions(stageCatalogue),
        [stageCatalogue],
    );
    const stageLabels = useMemo(
        () => stageLabelMap(stageCatalogue),
        [stageCatalogue],
    );

    // Queue counts + saved views for the left rail.
    const [queues, setQueues] = useState([]);
    const [savedViews, setSavedViews] = useState([]);
    const [savedViewsLoading, setSavedViewsLoading] = useState(false);
    // DEV-018 · Sales attribution options for the pipeline filters.
    // Loaded once on mount; failure is non-fatal — the picker falls
    // back to a plain "All / Unassigned" pair (no Sales users).
    const [salesOwners, setSalesOwners] = useState([]);
    const [salesSources, setSalesSources] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [qs, sv] = await Promise.all([
                    api.operationsQueues(),
                    api.operationsSavedViewsList(),
                ]);
                setQueues(qs || []);
                setSavedViews(sv || []);
            } catch (e) {
                // Non-fatal — the workspace still functions without the rail
                // populated. The rail then shows an "unavailable" note.
                console.warn("OPS-002 rail load failed", e);
            }
            try {
                const [owners, sources] = await Promise.all([
                    api.listSalesOwners().catch(() => ({ users: [] })),
                    api.listSalesSources().catch(() => ({ sources: [] })),
                ]);
                setSalesOwners((owners?.users) || []);
                setSalesSources((sources?.sources) || []);
            } catch (e) {
                console.warn("DEV-018 sales option load failed", e);
            }
        })();
    }, []);

    const setState = (next) => {
        const params = new URLSearchParams(searchParams);
        if (next === "all" || !VALID_STATE_QUERY.has(next)) {
            params.delete("state");
        } else {
            params.set("state", next);
        }
        setSearchParams(params);
    };

    // OPS-002 · Update a single URL param without disturbing others.
    const setUrlParam = (key, value) => {
        const params = new URLSearchParams(searchParams);
        if (value == null || value === "" || value === false) {
            params.delete(key);
        } else {
            params.set(key, String(value));
        }
        setSearchParams(params);
    };

    const applyQueue = (queueKey) => {
        // Selecting a built-in queue REPLACES the entire filter combination
        // — this is how the Reports Hub deep links and the sidebar clicks
        // both land on a clean, shareable URL. We only clear canonical
        // filters, so deep-link params (`project`, `reviewSurvey`) survive.
        const params = new URLSearchParams(searchParams);
        CANONICAL_FILTERS.forEach((k) => params.delete(k));
        if (queueKey) params.set("queue", queueKey);
        setSearchParams(params);
    };

    const applySavedView = (view) => {
        // Same rule as `applyQueue` — clear the whole canonical filter set
        // then rehydrate from the saved payload. Deep-link params are
        // preserved so a saved view never nukes an in-flight review-drawer.
        const params = new URLSearchParams(searchParams);
        CANONICAL_FILTERS.forEach((k) => params.delete(k));
        Object.entries(view.filters || {}).forEach(([k, v]) => {
            if (!CANONICAL_FILTERS.includes(k)) return;
            if (v != null && v !== "" && v !== false) params.set(k, String(v));
        });
        setSearchParams(params);
    };

    const resetFilters = () => {
        const params = new URLSearchParams(searchParams);
        CANONICAL_FILTERS.forEach((k) => params.delete(k));
        setSearchParams(params);
    };

    // OPS-002B · Iterate `BADGE_FILTERS` (the canonical registry minus `q`)
    // so brand-new filters always contribute to the badge without a
    // per-key change here. Memoised on `filterValues` for cheap re-render.
    const activeFilterCount = useMemo(
        () => BADGE_FILTERS.filter((k) => !!filterValues[k]).length,
        [filterValues],
    );

    // OPS-002B · Sync the local search string to URL `q` so it participates
    // in Saved Views and shareable URLs. Debounced to avoid a router update
    // on every keystroke.
    useEffect(() => {
        const handle = setTimeout(() => {
            if (query === urlQ) return;
            const params = new URLSearchParams(searchParams);
            if (query.trim()) params.set("q", query.trim());
            else params.delete("q");
            setSearchParams(params, { replace: true });
        }, 250);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            setLoadError(null);
            try {
                // OPS-002B · Any canonical filter (excluding `q`) delegates
                // to the server so canonical queue rules always win. If
                // ONLY `q` is set we still fall back to the light summary
                // endpoint + client-side substring match (fast + offline).
                const compound = BADGE_FILTERS.some((k) => !!filterValues[k]);
                if (compound) {
                    const res = await api.operationsProjectsQuery(filterValues);
                    setProjects(res.projects || []);
                } else {
                    const rows = await api.listProjectsSummary();
                    setProjects(rows || []);
                }
            } catch (e) {
                const msg = formatApiError(e?.response?.data?.detail) || "Failed to load projects";
                setLoadError(msg);
                toast.error(msg);
                setProjects([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [filterValues]);

    const reload = async () => {
        try {
            const rows = await api.listProjectsSummary();
            setProjects(rows || []);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to reload projects");
        }
    };

    const stateCounts = useMemo(() => {
        const c = { all: projects.length, active: 0, on_hold: 0, completed: 0 };
        projects.forEach((p) => {
            const s = p.state || "active";
            if (c[s] !== undefined) c[s] += 1;
        });
        return c;
    }, [projects]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return projects.filter((p) => {
            if (state !== "all" && (p.state || "active") !== state) return false;
            if (!q) return true;
            return (
                (p.customer_name || "").toLowerCase().includes(q) ||
                (p.address || "").toLowerCase().includes(q) ||
                (p.reference || "").toLowerCase().includes(q) ||
                (p.system_summary || "").toLowerCase().includes(q) ||
                (p.postcode || "").toLowerCase().includes(q)
            );
        });
    }, [projects, query, state]);

    const filterChips = STATE_CHIPS.map((chip) => ({
        ...chip,
        active: state === chip.key,
        count: stateCounts[chip.key] ?? 0,
    }));

    const selected = useMemo(
        () => projects.find((p) => p.id === selectedId) || null,
        [projects, selectedId],
    );

    // OPS-004B3B · Deep-link restoration.
    //
    // The list endpoints (`/projects/summary`, `/operations/projects/query`)
    // can legitimately exclude a project (state filter, scoped queue,
    // engineer scope). Without a direct-fetch fallback, opening a
    // valid `?project=<id>` URL then landed on the empty-detail
    // state — and refreshing lost the selection entirely. We now:
    //   1. Wait for `loading=false` (no verdict while data is loading).
    //   2. If the project is in the list → auto-select as before.
    //   3. Otherwise fetch it directly via `/api/projects/{id}` and,
    //      on success, splice it in front of the list and select
    //      it. A 404/403 leaves the deep-link untouched so the
    //      operator sees the empty-detail state rather than a
    //      silent redirect.
    //
    // OPS-008A · Stale-project deep-link error-loop fix.
    //   * A missing-project (404/403) previously left the URL
    //     unchanged AND surfaced a toast per fetch attempt. Because
    //     `projects` sits in this effect's dependency array, every
    //     list refresh re-triggered the fetch and enqueued another
    //     toast — hence the "continuously generates Project not
    //     found notifications" defect.
    //   * We now remember the failed id in a ref, surface exactly
    //     ONE controlled toast, remove the invalid `project=<id>`
    //     query param, clear stale selection, and never retry the
    //     same id in the same session. Refresh / share of a stale
    //     URL still safely rewrites to the plain list.
    const [deepLinkResolving, setDeepLinkResolving] = useState(false);
    const failedDeepLinkRef = useRef(new Set());
    useEffect(() => {
        if (!deepLinkProjectId || loading) return;
        if (selectedId === deepLinkProjectId) return;
        const found = projects.find((p) => p.id === deepLinkProjectId);
        if (found) {
            setSelectedId(found.id);
            return;
        }
        // Session-scoped guard: never retry an id we've already
        // proven to be missing during this mount.
        if (failedDeepLinkRef.current.has(deepLinkProjectId)) return;
        if (deepLinkResolving) return;
        setDeepLinkResolving(true);
        const attemptedId = deepLinkProjectId;
        (async () => {
            try {
                const fresh = await api.getProject(attemptedId);
                if (!fresh?.id) return;
                setProjects((prev) => {
                    if (prev.some((p) => p.id === fresh.id)) return prev;
                    return [fresh, ...prev];
                });
                setSelectedId(fresh.id);
            } catch (e) {
                // OPS-008A · Exactly one controlled failure per
                // missing id per mount. Strip the invalid deep-link
                // params, clear stale selection, and record the id
                // so subsequent list refreshes / poll cycles do not
                // re-enter this branch.
                failedDeepLinkRef.current.add(attemptedId);
                const status = e?.response?.status;
                const notFound = status === 404 || status === 403;
                toast.error(
                    notFound
                        ? "Project not found. It may have been deleted."
                        : (formatApiError(e?.response?.data?.detail)
                            || "Could not restore linked project"),
                );
                setSelectedId((prev) => (prev === attemptedId ? null : prev));
                setSearchParams((prev) => {
                    // React Router's functional updater guarantees
                    // we work off the LATEST URL so we don't wipe
                    // a filter the user just applied while this
                    // request was in flight.
                    if (prev.get("project") !== attemptedId) return prev;
                    const next = new URLSearchParams(prev);
                    next.delete("project");
                    next.delete("reviewSurvey");
                    next.delete("taskId");
                    next.delete("focusStage");
                    return next;
                }, { replace: true });
            } finally {
                setDeepLinkResolving(false);
            }
        })();
    }, [deepLinkProjectId, loading, projects, selectedId, deepLinkResolving, setSearchParams]);

    // OPS-DASHBOARD-DEEP-LINKS · Auto-select the FIRST filtered
    // result when a dashboard hero card lands here without an
    // explicit `?project=<id>`. The list keeps every matching row
    // visible; the URL gains `project=<first.id>` so refresh /
    // share / back all reproduce the same detail pane.
    useEffect(() => {
        if (loading) return;
        if (deepLinkProjectId) return;
        if (!hasDashboardFilter) return;
        if (selectedId) return;
        if (filtered.length === 0) return;
        const first = filtered[0];
        setSelectedId(first.id);
        const next = new URLSearchParams(searchParams);
        next.set("project", first.id);
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, deepLinkProjectId, hasDashboardFilter, selectedId, filtered]);

    // OPS-DASHBOARD-DEEP-LINKS · Zero-result guard. If the current
    // filter set matches nothing, clear any previously-set
    // `project=` deep-link so the detail pane doesn't display a
    // stale unrelated project. The `hasDashboardFilter` gate keeps
    // this from ever wiping a user's direct navigation.
    useEffect(() => {
        if (loading) return;
        if (!hasDashboardFilter) return;
        if (filtered.length > 0) return;
        if (!deepLinkProjectId && !selectedId) return;
        const next = new URLSearchParams(searchParams);
        next.delete("project");
        next.delete("taskId");
        next.delete("focusStage");
        setSearchParams(next, { replace: true });
        setSelectedId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, hasDashboardFilter, filtered.length]);

    const handleProjectUpdated = (updated) => {
        // UAT blocker fix · A hard-delete signals with `_deleted: true`;
        // drop the row from the list and clear selection.
        if (updated?._deleted) {
            setProjects((prev) => prev.filter((p) => p.id !== updated.id));
            if (selectedId === updated.id) setSelectedId(null);
            return;
        }
        setProjects((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    };

    // DEV-020 · When the operator picks a project we no longer keep them
    // inside the master-detail split; we navigate to the full-width
    // Single Project Focus View. `returnTo` carries the CURRENT URL so
    // the Back button restores every filter / saved view / search /
    // sort / scroll the user had. Browser Back keeps working natively.
    //
    // DEV-019 UAT correction 2 · Preserve the caller's Sales-Action
    // intent through to Focus. If the originating list URL carries
    // `focus_section=<key>` (dashboard action KPIs set this), forward
    // the value as an explicit URL query on the Focus route so the
    // Focus wrapper scrolls the correct section into view — and so
    // refresh / share reproduces the same behaviour.
    const onSelectProject = (p) => {
        setSelectedId(p?.id || null);
        if (deepLinkProjectId && (!p || p.id !== deepLinkProjectId)) {
            const next = new URLSearchParams(searchParams);
            next.delete("project");
            next.delete("reviewSurvey");
            next.delete("taskId");
            setSearchParams(next, { replace: true });
        }
        if (p?.id) {
            const returnTo = `${location.pathname}${location.search}`;
            const focusSection = searchParams.get("focus_section");
            const focusQuery = focusSection
                ? `?section=${encodeURIComponent(focusSection)}`
                : "";
            navigate(`/operations/projects/${p.id}${focusQuery}`, {
                state: { returnTo },
            });
        }
    };

    return (
        <>
        <OperationsMasterDetailPage
            eyebrow="Operations"
            title="Projects"
            icon={FolderOpen}
            backHref="/operations"
            backLabel="Back to Operations"
            primaryAction={canCreate ? (
                <button
                    type="button"
                    onClick={() => setCreateOpen(true)}
                    data-testid="ops-projects-new-btn"
                    className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md text-sm font-medium bg-[#131B2F] text-white hover:brightness-110 transition-all"
                >
                    <Plus size={14} strokeWidth={2.5} /> New project
                </button>
            ) : null}
            searchQuery={query}
            onSearchChange={setQuery}
            searchPlaceholder="Search customer, postcode, reference…"
            filterChips={filterChips}
            onFilterChipClick={setState}
            headerBanner={
                <Ops002Toolbar
                    queues={queues}
                    activeQueue={filterValues.queue}
                    activeFilterCount={activeFilterCount}
                    savedViews={savedViews}
                    searchParams={searchParams}
                    setUrlParam={setUrlParam}
                    stageFilterOptions={stageFilterOptions}
                    salesOwners={salesOwners}
                    salesSources={salesSources}
                    onSelectQueue={applyQueue}
                    onApplySavedView={applySavedView}
                    onResetFilters={resetFilters}
                    onSaveView={async (name) => {
                        try {
                            // OPS-002B · Only canonical filters make it into
                            // the payload — never deep-link params like
                            // `project` / `reviewSurvey`. Empty values are
                            // dropped so the payload stays diff-friendly.
                            const filters = {};
                            for (const k of CANONICAL_FILTERS) {
                                const v = searchParams.get(k);
                                if (v != null && v !== "") filters[k] = v;
                            }
                            const created = await api.operationsSavedViewCreate(name, filters);
                            setSavedViews((s) => [...s, created]);
                            toast.success(`Saved view "${created.name}"`);
                        } catch (e) {
                            toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
                        }
                    }}
                    onDeleteView={async (id) => {
                        try {
                            await api.operationsSavedViewDelete(id);
                            setSavedViews((s) => s.filter((v) => v.id !== id));
                            toast.success("View deleted");
                        } catch (e) {
                            toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
                        }
                    }}
                    onRenameView={async (id, newName) => {
                        try {
                            const updated = await api.operationsSavedViewUpdate(id, { name: newName });
                            setSavedViews((s) => s.map((v) => (v.id === id ? updated : v)));
                            toast.success("View renamed");
                        } catch (e) {
                            toast.error(formatApiError(e?.response?.data?.detail) || "Rename failed");
                        }
                    }}
                />
            }
            loading={loading}
            items={filtered}
            getItemId={(p) => p.id}
            emptyListState={loadError ? (
                <div className="p-8 text-center" data-testid="ops-projects-list-error">
                    <div className="text-sm text-[#B91C1C]">{loadError}</div>
                    <div className="mt-2 text-xs text-[#4B5565]">
                        Access to this list is restricted for your role, or the service is temporarily unavailable.
                    </div>
                </div>
            ) : undefined}
            listSummary={
                <span data-testid="ops-projects-summary">
                    {filtered.length} of {projects.length} projects · newest first
                </span>
            }
            renderItem={(p, { active, onSelect }) => (
                <ProjectRow project={p} active={active} onSelect={onSelect} />
            )}
            selectedId={selectedId}
            selectedItem={selected}
            onSelect={onSelectProject}
            renderDetailHeader={(p) => <DetailHeader project={p} />}
            renderDetail={(p) => (
                <OperationsProjectDetail
                    project={p}
                    onProjectUpdated={handleProjectUpdated}
                    reviewSurveyId={p && p.id === deepLinkProjectId ? reviewSurveyId : null}
                    focusTaskId={p && p.id === deepLinkProjectId ? focusTaskId : null}
                    focusStage={focusStage && !!selectedId}
                />
            )}
            testidPrefix="ops-projects"
        />
        {createOpen && (
            <NewProjectModal
                onClose={() => setCreateOpen(false)}
                onCreated={async (created) => {
                    setCreateOpen(false);
                    await reload();
                    if (created?.id) setSelectedId(created.id);
                }}
            />
        )}
        </>
    );
}


// ─── OPS-002 · Queue picker + saved-view toolbar ─────────────────────

function AdvFilter({ label, testid, value, options, onChange }) {
    return (
        <div className="inline-flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.1em] text-[#4B5565] font-medium">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className={`h-8 rounded-md border px-2 text-xs text-[#131B2F] focus:outline-none focus:ring-1 focus:ring-[#131B2F] ${
                    value ? "border-[#131B2F] bg-[#F5F5F7] font-medium" : "border-[#E5E7EB] bg-white"
                }`}
            >
                {options.map(([v, l]) => (
                    <option key={v || "__all__"} value={v}>{l}</option>
                ))}
            </select>
        </div>
    );
}

function Ops002Toolbar({
    queues, activeQueue, activeFilterCount, savedViews,
    searchParams, setUrlParam,
    stageFilterOptions,
    salesOwners = [], salesSources = [],
    onSelectQueue, onApplySavedView, onResetFilters,
    onSaveView, onDeleteView, onRenameView,
}) {
    const [saveOpen, setSaveOpen] = useState(false);
    const [saveName, setSaveName] = useState("");
    return (
        <div
            className="px-4 md:px-5 py-3 border-b border-[#F1F5F9] bg-white flex flex-wrap items-center gap-2"
            data-testid="ops-projects-ops002-toolbar"
        >
            <label className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565] font-medium">
                Queue
            </label>
            <select
                value={activeQueue || ""}
                onChange={(e) => onSelectQueue(e.target.value || null)}
                data-testid="ops-projects-queue-select"
                className="h-9 rounded-md border border-[#E5E7EB] bg-white px-3 text-sm text-[#131B2F] focus:outline-none focus:ring-2 focus:ring-[#131B2F]"
            >
                <option value="">All projects</option>
                {(queues || []).map((q) => (
                    <option key={q.key} value={q.key}>
                        {q.label} ({q.count})
                    </option>
                ))}
            </select>

            {/* OPS-002A · Advanced Filters (individual, combinable). */}
            <AdvFilter label="Assigned"  testid="ops-projects-filter-assigned"
                       value={searchParams.get("assigned") || ""}
                       options={[["", "Any"], ["true", "Assigned"], ["false", "Unassigned"]]}
                       onChange={(v) => setUrlParam("assigned", v)} />
            <AdvFilter label="Overdue"   testid="ops-projects-filter-overdue"
                       value={searchParams.get("overdue") === "true" ? "true" : ""}
                       options={[["", "Any"], ["true", "Overdue only"]]}
                       onChange={(v) => setUrlParam("overdue", v === "true" ? "true" : null)} />
            <AdvFilter label="Survey"    testid="ops-projects-filter-survey"
                       value={searchParams.get("survey") || ""}
                       options={[["", "Any"], ["not_started", "Not started"],
                                 ["submitted", "Submitted"],
                                 ["returned_for_clarification", "Clarification"],
                                 ["approved", "Approved"]]}
                       onChange={(v) => setUrlParam("survey", v)} />
            <AdvFilter label="DNO"       testid="ops-projects-filter-dno"
                       value={searchParams.get("dno") || ""}
                       options={[["", "Any"], ["waiting", "Waiting"]]}
                       onChange={(v) => setUrlParam("dno", v)} />
            <AdvFilter label="Logistics" testid="ops-projects-filter-logistics"
                       value={searchParams.get("logistics") || ""}
                       options={[["", "Any"], ["outstanding", "Outstanding"]]}
                       onChange={(v) => setUrlParam("logistics", v)} />
            <AdvFilter label="Customer action" testid="ops-projects-filter-customer-action"
                       value={searchParams.get("customer_action") || ""}
                       options={[["", "Any"], ["required", "Required"]]}
                       onChange={(v) => setUrlParam("customer_action", v)} />
            <AdvFilter label="Install window" testid="ops-projects-filter-window"
                       value={searchParams.get("window") || ""}
                       options={[["", "Any"], ["14d", "Next 14 days"]]}
                       onChange={(v) => setUrlParam("window", v)} />

            {/* OPS-004B · Canonical stage filters (SSOT in
                `lib/stages.js`). Every option label is human-readable
                — never a raw stage_key. */}
            <AdvFilter label="Stage"          testid="ops-projects-filter-current-stage"
                       value={searchParams.get("current_stage") || ""}
                       options={stageFilterOptions}
                       onChange={(v) => setUrlParam("current_stage", v)} />
            <AdvFilter label="Stage SLA"      testid="ops-projects-filter-stage-sla"
                       value={searchParams.get("stage_sla_status") || ""}
                       options={SLA_FILTER_OPTIONS}
                       onChange={(v) => setUrlParam("stage_sla_status", v)} />
            <AdvFilter label="Stalled"        testid="ops-projects-filter-stalled"
                       value={searchParams.get("stalled_only") === "true" ? "true" : ""}
                       options={[["", "Any"], ["true", "Stalled only"]]}
                       onChange={(v) => setUrlParam("stalled_only", v === "true" ? "true" : null)} />

            {/* DEV-018 · Sales Owner + Sales Source filters. Both admit
                the ``unassigned`` sentinel alongside the canonical
                ids/keys so operators can slice historical/imported
                rows without a fresh dashboard. */}
            <AdvFilter label="Sales Owner" testid="ops-projects-filter-sales-owner"
                       value={searchParams.get("sales_owner") || ""}
                       options={[
                           ["", "Any"], ["unassigned", "Unassigned"],
                           ...salesOwners.map((u) => [u.id, u.name || u.email || u.id]),
                       ]}
                       onChange={(v) => setUrlParam("sales_owner", v)} />
            <AdvFilter label="Sales Source" testid="ops-projects-filter-sales-source"
                       value={searchParams.get("sales_source") || ""}
                       options={[
                           ["", "Any"], ["unassigned", "Unassigned"],
                           ...(salesSources.length
                               ? salesSources.map((s) => [s.key, s.label])
                               : Object.entries(SALES_SOURCE_LABELS_FALLBACK)),
                       ]}
                       onChange={(v) => setUrlParam("sales_source", v)} />

            {/* DEV-019 · Sales workflow filters. Compound cleanly with
                DEV-018 owner/source and every existing pipeline filter. */}
            <AdvFilter label="Outcome" testid="ops-projects-filter-outcome"
                       value={searchParams.get("outcome") || ""}
                       options={[
                           ["", "Any"], ["active", "Active"], ["won", "Won"], ["lost", "Lost"],
                       ]}
                       onChange={(v) => setUrlParam("outcome", v)} />
            <AdvFilter label="Action Due" testid="ops-projects-filter-action-due"
                       value={searchParams.get("action_due") || ""}
                       options={[
                           ["", "Any"], ["today", "Due today"],
                           ["overdue", "Overdue"], ["missing", "Missing next action"],
                       ]}
                       onChange={(v) => setUrlParam("action_due", v)} />

            {/* DEV-019 UAT3 · Sales lifecycle stage filter. */}
            <AdvFilter label="Sales Stage" testid="ops-projects-filter-sales-stage"
                       value={searchParams.get("sales_stage") || ""}
                       options={[
                           ["", "Any"], ["unassigned", "Unassigned"],
                           ["enquiry", "Enquiry"],
                           ["contacted_qualified", "Contacted / Qualified"],
                           ["sales_appointment", "Sales Survey / Appointment"],
                           ["quote_proposal", "Quote / Proposal"],
                           ["decision", "Decision"],
                       ]}
                       onChange={(v) => setUrlParam("sales_stage", v)} />

            {activeFilterCount > 0 && (
                <>
                    <span
                        className="ml-2 text-[11px] text-[#4B5565]"
                        data-testid="ops-projects-active-filter-summary"
                    >
                        {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} active
                    </span>
                    <button
                        type="button"
                        onClick={onResetFilters}
                        data-testid="ops-projects-reset-filters"
                        className="text-[11px] text-[#131B2F] hover:underline"
                    >
                        Reset
                    </button>
                    <button
                        type="button"
                        onClick={() => { setSaveName(""); setSaveOpen(true); }}
                        data-testid="ops-projects-save-view-btn"
                        className="ml-2 h-8 px-3 rounded-md border border-[#131B2F] text-[#131B2F] text-xs hover:bg-[#F5F5F7]"
                    >
                        Save as view
                    </button>
                </>
            )}

            <div className="ml-auto flex items-center gap-2 flex-wrap" data-testid="ops-projects-saved-views">
                <label className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565] font-medium">
                    My views
                </label>
                {(savedViews || []).length === 0 ? (
                    <span className="text-[11px] text-[#4B5565]">None yet</span>
                ) : (
                    (savedViews || []).map((v) => (
                        <span
                            key={v.id}
                            className="inline-flex items-center gap-1 rounded-full bg-[#E1E6F0] text-[#131B2F] text-[11px]"
                            data-testid={`ops-projects-saved-view-${v.id}`}
                        >
                            <button
                                type="button"
                                onClick={() => onApplySavedView(v)}
                                className="pl-2.5 py-1 hover:underline"
                                data-testid={`ops-projects-saved-view-apply-${v.id}`}
                            >
                                {v.name}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const next = window.prompt(`Rename "${v.name}" to`, v.name);
                                    if (next && next.trim() && next !== v.name) onRenameView(v.id, next.trim());
                                }}
                                title="Rename"
                                data-testid={`ops-projects-saved-view-rename-${v.id}`}
                                className="px-1 text-[#4B5565] hover:text-[#131B2F]"
                            >
                                ✎
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (window.confirm(`Delete saved view "${v.name}"?`)) onDeleteView(v.id);
                                }}
                                title="Delete"
                                data-testid={`ops-projects-saved-view-delete-${v.id}`}
                                className="pr-2.5 text-[#4B5565] hover:text-[#E53E3E]"
                            >
                                ×
                            </button>
                        </span>
                    ))
                )}
            </div>

            {saveOpen && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" data-testid="ops-projects-save-view-modal">
                    <div className="bg-white rounded-xl p-6 w-full max-w-sm">
                        <div className="text-base text-[#131B2F] font-medium mb-3">Save current filters</div>
                        <input
                            autoFocus
                            data-testid="ops-projects-save-view-name-input"
                            value={saveName}
                            onChange={(e) => setSaveName(e.target.value)}
                            placeholder="e.g. Overdue installs — engineer Sam"
                            className="w-full h-10 rounded-md border border-[#E5E7EB] px-3 text-sm text-[#131B2F] outline-none focus:border-[#131B2F]"
                        />
                        <div className="mt-4 flex gap-2">
                            <button
                                type="button"
                                onClick={() => setSaveOpen(false)}
                                className="flex-1 h-9 rounded-md border border-[#E5E7EB] text-sm text-[#131B2F]"
                                data-testid="ops-projects-save-view-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    if (!saveName.trim()) return;
                                    await onSaveView(saveName.trim());
                                    setSaveOpen(false);
                                }}
                                className="flex-1 h-9 rounded-md bg-[#131B2F] text-white text-sm"
                                data-testid="ops-projects-save-view-confirm"
                            >
                                Save view
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


// ─── List row ──────────────────────────────────────────────────────────

function ProjectRow({ project, active, onSelect }) {
    const state = project.state || "active";
    // OPS-004B1 · The row `stage_label` is server-hydrated via
    // `hydrate_stage_view`. The frontend never carries a parallel
    // stage→label map — falls back to the raw canonical key if a
    // row somehow arrives without a hydrated label.
    const stageKey = project.current_stage || "unknown";
    const stageLabel = project.stage_label || stageKey;
    const sla = SLA_STATUS_LABELS[project.stage_sla_status] || null;
    const stalled = !!project.is_stage_stalled;
    return (
        <button
            type="button"
            onClick={onSelect}
            data-testid={`ops-projects-list-item-${project.id}`}
            aria-current={active ? "true" : "false"}
            className={`w-full text-left px-4 md:px-5 py-3.5 flex items-start gap-3 transition-colors border-l-4 ${
                active
                    ? "bg-[#F5F7FB] border-[#131B2F]"
                    : "border-transparent hover:bg-[#FAFAFC]"
            }`}
        >
            <div className={`w-9 h-9 rounded-md ${active ? "bg-[#131B2F] text-white" : "bg-[#E1E6F0] text-[#131B2F]"} flex items-center justify-center shrink-0`}>
                <FolderOpen size={16} strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <div className="text-sm font-medium text-[#131B2F] truncate">
                        {project.customer_name || "Customer"}
                    </div>
                    <span className="text-[10px] font-mono text-[#4B5565] shrink-0">{project.reference}</span>
                </div>
                <div className="mt-0.5 text-xs text-[#4B5565] truncate flex items-center gap-1">
                    <MapPin size={11} strokeWidth={1.75} /> {project.address || "—"}
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1.5 text-[11px] text-[#131B2F]`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${STATE_DOT[state] || STATE_DOT.active}`} />
                        {STATE_LABEL[state] || state}
                    </span>
                    <span className="text-[11px] text-[#4B5565]">·</span>
                    {/* OPS-004B · Stage summary chip cluster. */}
                    <span
                        data-testid={`ops-projects-list-stage-${project.id}`}
                        className="text-[11px] text-[#4B5565] truncate"
                    >
                        {stageLabel}
                        {project.time_in_stage && (
                            <span
                                data-testid={`ops-projects-list-stage-tis-${project.id}`}
                                className="ml-1 text-[#4B5565]"
                            >· {formatTimeInStage(project.time_in_stage)}</span>
                        )}
                    </span>
                    {/* DEV-018 · Sales Owner + Sales Source chips. Render
                        the row's commercial attribution inline so
                        operators can see it without opening detail. Only
                        rendered when a value is present — Unassigned
                        rows stay clean. */}
                    {project.sales_owner_name && (
                        <span
                            data-testid={`ops-projects-list-sales-owner-${project.id}`}
                            className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#EEF2FF] text-[#1E3A8A] border border-[#DBE1F7]"
                            title={`Sales Owner: ${project.sales_owner_name}`}
                        >
                            {project.sales_owner_name}
                        </span>
                    )}
                    {project.sales_source && (
                        <span
                            data-testid={`ops-projects-list-sales-source-${project.id}`}
                            className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#F5F5F7] text-[#4B5565] border border-[#E5E7EB]"
                            title="Sales Source"
                        >
                            {SALES_SOURCE_LABELS_FALLBACK[project.sales_source] || project.sales_source}
                        </span>
                    )}
                    {/* DEV-019 · Outcome + Next Action chips. */}
                    {project.opportunity_outcome && project.opportunity_outcome !== "active" && (
                        <span
                            data-testid={`ops-projects-list-outcome-${project.id}`}
                            className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${
                                project.opportunity_outcome === "won"
                                    ? "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]"
                                    : "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]"
                            }`}
                        >
                            {project.opportunity_outcome === "won" ? "Won" : "Lost"}
                        </span>
                    )}
                    {project.next_sales_action?.due_date && (() => {
                        const today = new Date().toISOString().slice(0, 10);
                        const due = String(project.next_sales_action.due_date).slice(0, 10);
                        const tone = due < today ? "overdue" : due === today ? "today" : "upcoming";
                        const cls = tone === "overdue"
                            ? "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]"
                            : tone === "today"
                                ? "bg-[#FEF3C7] text-[#B45309] border-[#FCD34D]"
                                : "bg-[#F5F5F7] text-[#4B5565] border-[#E5E7EB]";
                        return (
                            <span
                                data-testid={`ops-projects-list-action-${project.id}`}
                                className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${cls}`}
                                title={`Next: ${project.next_sales_action.action_type} · Due ${due}`}
                            >
                                {tone === "overdue" ? "Overdue action" : tone === "today" ? "Due today" : `Due ${due}`}
                            </span>
                        );
                    })()}
                    {sla && sla.tone !== "neutral" && (
                        <span
                            data-testid={`ops-projects-list-sla-${project.id}`}
                            className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${
                                sla.tone === "green"
                                    ? "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]"
                                    : sla.tone === "amber"
                                    ? "bg-[#FEF3C7] text-[#B45309] border-[#FCD34D]"
                                    : "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]"
                            }`}
                        >
                            {sla.label}
                        </span>
                    )}
                    {stalled && (
                        <span
                            data-testid={`ops-projects-list-stalled-${project.id}`}
                            className="w-1.5 h-1.5 rounded-full bg-[#B91C1C]"
                            title="Stalled — exceeded stall threshold"
                            aria-label="Stalled stage"
                        />
                    )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1 rounded-full bg-[#F0F1F5] overflow-hidden">
                        <div className="h-full bg-[#3E9E2E]" style={{ width: `${project.progress || 0}%` }} />
                    </div>
                    <span className="text-[11px] text-[#4B5565] tabular-nums">{project.progress || 0}%</span>
                </div>
            </div>
        </button>
    );
}


// ─── Detail pane header + body ─────────────────────────────────────────

function DetailHeader({ project }) {
    const state = project.state || "active";
    return (
        <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
                <div className="text-[10px] tracking-[0.14em] uppercase text-[#4B5565] font-medium flex items-center gap-1.5">
                    <FolderOpen size={11} strokeWidth={1.75} /> {project.reference}
                </div>
                <h2 className="mt-1 text-lg md:text-xl font-semibold text-[#131B2F] truncate">
                    {project.customer_name}
                </h2>
                <div className="mt-1 text-xs text-[#4B5565] truncate flex items-center gap-1.5">
                    <MapPin size={12} strokeWidth={1.75} /> {project.address || "—"}
                </div>
            </div>
            <div className="text-right shrink-0">
                <span className="inline-flex items-center gap-1.5 text-[11px] text-[#131B2F] px-2 py-1 rounded-full bg-[#F5F5F7]">
                    <span className={`w-1.5 h-1.5 rounded-full ${STATE_DOT[state] || STATE_DOT.active}`} />
                    {STATE_LABEL[state] || state}
                </span>
                <div className="mt-2 text-[11px] text-[#4B5565]">Progress</div>
                <div className="text-lg font-semibold text-[#131B2F] tabular-nums">{project.progress || 0}%</div>
            </div>
        </div>
    );
}



// ─── New project modal ─────────────────────────────────────────────────
// Restored from the pre-DEV-003 Installer dashboard (commit 7685f4d
// removed the trigger; the API and modal shape live on unchanged).
// Gated by the `manage_projects` permission on both the backend
// (`POST /projects`) and the caller (see `canCreate` above).

function NewProjectModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        customer_name: "",
        customer_email: "",
        system_summary: "",
        address: "",
        estimated_completion: "",
        // DEV-018 · Optional commercial attribution captured at creation.
        sales_owner_user_id: "",
        sales_source: "",
        sales_source_detail: "",
    });
    const [err, setErr] = useState("");
    const [saving, setSaving] = useState(false);
    // DEV-018 · Load Sales Owner + Sales Source options once when the
    // modal opens. Failures are non-fatal: the operator can still
    // create a project with "Unassigned" attribution.
    const [salesOwners, setSalesOwners] = useState([]);
    const [salesSources, setSalesSources] = useState([]);
    useEffect(() => {
        (async () => {
            try {
                const [owners, sources] = await Promise.all([
                    api.listSalesOwners().catch(() => ({ users: [] })),
                    api.listSalesSources().catch(() => ({ sources: [] })),
                ]);
                setSalesOwners((owners?.users) || []);
                setSalesSources((sources?.sources) || []);
            } catch { /* non-fatal */ }
        })();
    }, []);

    const submit = async () => {
        setErr("");
        if (!form.customer_name || !form.customer_email || !form.system_summary || !form.address) {
            setErr("All fields except completion date are required");
            return;
        }
        if (form.sales_source === "other" && !(form.sales_source_detail || "").trim()) {
            setErr("Sales Source Detail is required when Sales Source = Other.");
            return;
        }
        setSaving(true);
        try {
            const created = await api.createProject({
                customer_name: form.customer_name,
                customer_email: form.customer_email,
                system_summary: form.system_summary,
                address: form.address,
                estimated_completion: form.estimated_completion || "TBD",
                sales_owner_user_id: form.sales_owner_user_id || null,
                sales_source: form.sales_source || null,
                sales_source_detail: (form.sales_source_detail || "").trim() || null,
            });
            toast.success("Project created");
            onCreated(created);
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const fields = [
        ["customer_name", "Customer name"],
        ["customer_email", "Customer email"],
        ["system_summary", "System summary"],
        ["address", "Address"],
        ["estimated_completion", "Estimated completion"],
    ];

    return (
        <div
            className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center"
            data-testid="ops-new-project-modal"
        >
            <div className="bg-white w-full md:max-w-lg md:rounded-3xl rounded-t-3xl p-6 md:p-8 max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-xl text-[#131B2F] font-medium">New project</div>
                        <div className="text-sm text-[#4B5565] mt-0.5">
                            Creates a customer account with a randomly-generated password if one doesn&rsquo;t exist. The customer sets their own password via the forgot-password flow before first sign-in.
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-full bg-[#F5F5F7] text-[#4B5565] flex items-center justify-center"
                        aria-label="Close"
                        data-testid="ops-new-project-close"
                    >
                        <X size={16} strokeWidth={1.75} />
                    </button>
                </div>
                <div className="mt-5 space-y-3.5">
                    {fields.map(([k, label]) => (
                        <div key={k}>
                            <label className="block text-sm text-[#4B5565] mb-1.5">{label}</label>
                            <input
                                data-testid={`ops-new-project-${k}`}
                                value={form[k]}
                                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                type={k === "customer_email" ? "email" : "text"}
                                className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E]"
                            />
                        </div>
                    ))}
                    {/* DEV-018 · Commercial attribution pickers. Both
                        default to Unassigned so a historical/imported
                        project is never blocked by these fields. Detail
                        only shows/requires when Source = Other. */}
                    <div>
                        <label className="block text-sm text-[#4B5565] mb-1.5">Sales Owner</label>
                        <select
                            data-testid="ops-new-project-sales-owner"
                            value={form.sales_owner_user_id}
                            onChange={(e) => setForm({ ...form, sales_owner_user_id: e.target.value })}
                            className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E]"
                        >
                            <option value="">Unassigned</option>
                            {salesOwners.map((u) => (
                                <option key={u.id} value={u.id}
                                    data-testid={`ops-new-project-sales-owner-opt-${u.id}`}>
                                    {u.name || u.email || u.id}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm text-[#4B5565] mb-1.5">Sales Source</label>
                        <select
                            data-testid="ops-new-project-sales-source"
                            value={form.sales_source}
                            onChange={(e) => setForm({ ...form, sales_source: e.target.value })}
                            className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E]"
                        >
                            <option value="">Unassigned</option>
                            {(salesSources.length
                                ? salesSources
                                : Object.entries(SALES_SOURCE_LABELS_FALLBACK).map(([key, label]) => ({ key, label }))
                            ).map((s) => (
                                <option key={s.key} value={s.key}
                                    data-testid={`ops-new-project-sales-source-opt-${s.key}`}>
                                    {s.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    {form.sales_source && (
                        <div>
                            <label className="block text-sm text-[#4B5565] mb-1.5">
                                Sales Source Detail{form.sales_source === "other" ? " (required)" : " (optional)"}
                            </label>
                            <input
                                data-testid="ops-new-project-sales-source-detail"
                                value={form.sales_source_detail}
                                onChange={(e) => setForm({ ...form, sales_source_detail: e.target.value })}
                                type="text"
                                maxLength={280}
                                placeholder="Optional supporting context…"
                                className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E]"
                            />
                        </div>
                    )}
                    {!!err && (
                        <div className="text-sm text-[#E53E3E]" data-testid="ops-new-project-error">
                            {err}
                        </div>
                    )}
                    <div className="flex gap-2 pt-2">
                        <button
                            className="pe-btn-outline flex-1 justify-center h-11"
                            onClick={onClose}
                            data-testid="ops-new-project-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            className="pe-btn-primary flex-1 justify-center h-11"
                            onClick={submit}
                            disabled={saving}
                            data-testid="ops-new-project-submit"
                        >
                            {saving ? "Creating…" : "Create"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
